import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { INITIAL_LAST_ID, normalizeLastId } from "../utils/loadMore";

const DEFAULT_LOAD_MORE_BATCH_SIZE = 10;

function defaultGetItemId(item) {
  return (
    item?.id ??
    item?.Id ??
    item?.ID ??
    item?.TransactionID ??
    item?.TransactionId ??
    item?.Transaction ??
    item?.OrderID ??
    item?.TransferID ??
    item?.Transfer ??
    item?.statementDetailID ??
    item?.StatementDetailID ??
    item?.Reference ??
    null
  );
}

function normalizeRows(result) {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.rows)) return result.rows;
  if (Array.isArray(result?.data)) return result.data;
  if (Array.isArray(result?.Payload)) return result.Payload;
  return [];
}

function mergeUniqueRows(currentRows, nextRows, getItemId, dedupe) {
  if (!dedupe) {
    return {
      rows: [...currentRows, ...nextRows],
      addedCount: nextRows.length,
    };
  }

  const seenIds = new Set(
    currentRows
      .map((row) => getItemId(row))
      .filter((id) => id !== null && id !== undefined && id !== ""),
  );
  const uniqueRows = [];

  nextRows.forEach((row) => {
    const id = getItemId(row);

    if (id === null || id === undefined || id === "") {
      uniqueRows.push(row);
      return;
    }

    if (seenIds.has(id)) return;
    seenIds.add(id);
    uniqueRows.push(row);
  });

  return {
    rows: [...currentRows, ...uniqueRows],
    addedCount: uniqueRows.length,
  };
}

export default function useLoadMoreList({
  apiFunction,
  getLastId = defaultGetItemId,
  getCursor,
  getItemId,
  cursorKey = "lastId",
  initialCursor,
  resetKey = "",
  enabled = true,
  autoLoad = true,
  dedupe = true,
  minimumBatchSize = DEFAULT_LOAD_MORE_BATCH_SIZE,
}) {
  const apiFunctionRef = useRef(apiFunction);
  const getCursorRef = useRef(getCursor || getLastId);
  const getItemIdRef = useRef(getItemId || getLastId);
  const itemsRef = useRef([]);
  const requestIdRef = useRef(0);

  const [items, setItemsState] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [loadMoreError, setLoadMoreError] = useState(null);

  apiFunctionRef.current = apiFunction;
  getCursorRef.current = getCursor || getLastId;
  getItemIdRef.current = getItemId || getLastId;

  const getInitialCursor = useCallback(() => {
    if (initialCursor !== undefined) return initialCursor;
    return cursorKey === "lastId" ? INITIAL_LAST_ID : null;
  }, [cursorKey, initialCursor]);

  const normalizeCursor = useCallback(
    (value) => (cursorKey === "lastId" ? normalizeLastId(value) : value ?? null),
    [cursorKey],
  );

  const setItems = useCallback((value) => {
    setItemsState((current) => {
      const nextValue =
        typeof value === "function" ? value(current) : value;
      const safeValue = Array.isArray(nextValue) ? nextValue : [];
      itemsRef.current = safeValue;
      return safeValue;
    });
  }, []);

  const reset = useCallback(() => {
    requestIdRef.current += 1;
    itemsRef.current = [];
    setItemsState([]);
    setError(null);
    setLoadMoreError(null);
    setHasMore(true);
    setIsInitialLoading(false);
    setIsLoadingMore(false);
  }, []);

  const fetchRows = useCallback(
    async ({ append }) => {
      if (!enabled || typeof apiFunctionRef.current !== "function") return [];

      const currentItems = itemsRef.current;
      const lastItem = currentItems[currentItems.length - 1];
      const cursor = normalizeCursor(
        append && lastItem
          ? getCursorRef.current(lastItem)
          : getInitialCursor(),
      );

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      if (append) {
        setLoadMoreError(null);
        setIsLoadingMore(true);
      } else {
        setError(null);
        setLoadMoreError(null);
        setIsInitialLoading(true);
      }

      try {
        const result = await apiFunctionRef.current({
          lastId: cursorKey === "lastId" ? cursor : undefined,
          [cursorKey]: cursor,
          cursor,
          append,
          currentItems,
        });
        const nextRows = normalizeRows(result);

        if (requestIdRef.current !== requestId) return [];

        if (append) {
          const merged = mergeUniqueRows(
            currentItems,
            nextRows,
            getItemIdRef.current,
            dedupe,
          );
          itemsRef.current = merged.rows;
          setItemsState(merged.rows);

          setHasMore(
            nextRows.length >= minimumBatchSize && merged.addedCount > 0,
          );
        } else {
          itemsRef.current = nextRows;
          setItemsState(nextRows);

          setHasMore(nextRows.length >= minimumBatchSize);
        }

        return nextRows;
      } catch (fetchError) {
        if (requestIdRef.current === requestId) {
          if (append) {
            setLoadMoreError(fetchError);
            setHasMore(currentItems.length > 0);
          } else {
            setError(fetchError);
            itemsRef.current = [];
            setItemsState([]);
            setHasMore(false);
          }
        }
        if (append) return [];
        throw fetchError;
      } finally {
        if (requestIdRef.current === requestId) {
          setIsInitialLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [
      cursorKey,
      dedupe,
      enabled,
      getInitialCursor,
      minimumBatchSize,
      normalizeCursor,
    ],
  );

  const refresh = useCallback(() => fetchRows({ append: false }), [fetchRows]);
  const loadMore = useCallback(() => {
    if (isInitialLoading || isLoadingMore || !hasMore) return Promise.resolve([]);
    return fetchRows({ append: true });
  }, [fetchRows, hasMore, isInitialLoading, isLoadingMore]);

  useEffect(() => {
    if (!enabled) {
      reset();
      return;
    }

    if (autoLoad) {
      itemsRef.current = [];
      setItemsState([]);
      setHasMore(true);
      setError(null);
      setLoadMoreError(null);
      fetchRows({ append: false }).catch(() => {});
    }
  }, [autoLoad, enabled, fetchRows, reset, resetKey]);

  const state = useMemo(
    () => ({
      items,
      setItems,
      isInitialLoading,
      isLoadingMore,
      hasMore,
      loadMoreError,
      error,
      loadMore,
      refresh,
      reset,
    }),
    [
      error,
      hasMore,
      isInitialLoading,
      isLoadingMore,
      items,
      loadMore,
      loadMoreError,
      refresh,
      reset,
      setItems,
    ],
  );

  return state;
}
